import React, { useState, useEffect } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "@/redux/slices/categorySlice";
import debounce from "lodash/debounce";

const Sidebar = ({ onFilterChange }) => {
  const [range, setRange] = useState([0, 10000000]); // Giá mặc định từ 0 đến 10 triệu
  const [isExpanded, setIsExpanded] = useState(true);
  const [localGuideExpanded, setLocalGuideExpanded] = useState(true);
  const [tourTypeExpanded, setTourTypeExpanded] = useState(true);
  const [reviewsExpanded, setReviewsExpanded] = useState(true);
  const [discountExpanded, setDiscountExpanded] = useState(true);
  const [newestExpanded, setNewestExpanded] = useState(true);
  const [bestSellerExpanded, setBestSellerExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const dispatch = useDispatch();
  const { categories, loading, error } = useSelector((state) => state.category);

  const formatVND = (number) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(number);
  };

  const handleRangeChange = (newRange) => {
    setRange(newRange);
    applyFilters();
  };

  const handleReset = () => {
    setRange([0, 10000000]); // Reset về 0 - 10 triệu
    setSearchQuery("");
    document
      .querySelectorAll('input[type="checkbox"]')
      .forEach((el) => (el.checked = false));
    document
      .querySelectorAll('input[type="text"]')
      .forEach((el) => (el.value = ""));
    applyFilters();
  };

  // Debounce cho việc gọi API, chờ 800ms sau khi người dùng dừng nhập
  const debouncedApplyFilters = debounce((filters) => {
    onFilterChange(filters);
  }, 800);

  const applyFilters = () => {
    const discountMap = {
      discount1: "10",
      discount2: "20",
      discount3: "30",
    };

    const bestsellerMap = {
      bestseller1: "10",
      bestseller2: "20",
      bestseller3: "50",
    };

    const filters = {
      search: searchQuery,
      priceMin: range[0],
      priceMax: range[1],
      categories: Array.from(
        document.querySelectorAll('input[name^="category_"]:checked')
      ).map((el) => el.name.replace("category_", "")),
      discounts: Array.from(
        document.querySelectorAll('input[name^="discount"]:checked')
      ).map((el) => discountMap[el.id]),
      newest: Array.from(
        document.querySelectorAll('input[name^="newest"]:checked')
      ).map((el) => el.id.replace("newest", "")),
      bestsellers: Array.from(
        document.querySelectorAll('input[name^="bestseller"]:checked')
      ).map((el) => bestsellerMap[el.id]),
      reviews: Array.from(
        document.querySelectorAll('input[name^="review"]:checked')
      ).map((el) => el.id.replace("review", "")),
    };
    debouncedApplyFilters(filters);
  };

  // Cập nhật searchQuery nhưng không gọi API ngay, chờ debounce
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    applyFilters(); // Gọi applyFilters, nhưng API chỉ chạy sau 800ms nhờ debounce
  };

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    applyFilters();
  }, []);

  const sliderStyles = `
    .custom-slider .rc-slider { margin: 0 10px; }
    .custom-slider .rc-slider-rail { height: 6px; background-color: #e9ecef; }
    .custom-slider .rc-slider-track { height: 6px; background-color: #007bff; }
    .custom-slider .rc-slider-handle { width: 20px; height: 20px; border: 2px solid #007bff; background-color: #fff; margin-top: -7px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .custom-slider .rc-slider-handle:hover { border-color: #0056b3; }
    .price-tooltip { position: absolute; top: -30px; transform: translateX(-50%); background-color: #333; color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; white-space: nowrap; }
    .price-range { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  `;

  // Giữ thanh cuộn như cũ với width: 6px
  const categoryStyles = `
    .category-list {
      max-height: 300px; /* Giới hạn chiều cao tối đa */
      overflow-y: auto; /* Thêm thanh cuộn dọc khi vượt quá chiều cao */
      padding-right: 10px; /* Khoảng cách bên phải để tránh chồng lấp thanh cuộn */
    }
    .category-list::-webkit-scrollbar {
      width: 6px; /* Độ rộng thanh cuộn giữ nguyên như cũ */
    }
    .category-list::-webkit-scrollbar-thumb {
      background-color: #007bff; /* Màu thanh cuộn */
      border-radius: 3px;
    }
    .category-list::-webkit-scrollbar-track {
      background-color: #e9ecef; /* Màu nền thanh cuộn */
    }
  `;

  return (
    <div className="col-xl-3 col-lg-4 theiaStickySidebar">
      <style>{sliderStyles}</style>
      <style>{categoryStyles}</style>
      <div className="card filter-sidebar mb-4 mb-lg-0">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5>Bộ lọc</h5>
          <div>
            <button
              className="btn btn-light btn-sm me-2"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Ẩn bộ lọc" : "Hiện bộ lọc"}
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleReset}>
              Đặt lại
            </button>
          </div>
        </div>
        {isExpanded && (
          <div className="card-body p-0">
            <form onChange={applyFilters}>
              <div className="p-3 border-bottom">
                <label className="form-label fs-16">Tìm kiếm tour</label>
                <div className="input-icon">
                  <span className="input-icon-addon">
                    <i className="isax isax-search-normal"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Tìm kiếm tour..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className="accordion accordion-list">
                <div className="accordion-item border-bottom p-3">
                  <div className="accordion-header">
                    <button
                      className="accordion-button p-0"
                      type="button"
                      onClick={() => setLocalGuideExpanded(!localGuideExpanded)}
                    >
                      <i className="isax isax-coin me-2 text-primary"></i>Giá cả
                    </button>
                  </div>
                  {localGuideExpanded && (
                    <div className="accordion-body mt-4">
                      <div className="filter-range custom-slider">
                        <Slider
                          range
                          min={0}
                          max={100000000}
                          defaultValue={[0, 10000000]}
                          value={range}
                          onChange={handleRangeChange}
                          tipFormatter={formatVND}
                          tipProps={{
                            placement: "top",
                            visible: true,
                            getTooltipContainer: (node) => node.parentNode,
                            overlayClassName: "price-tooltip",
                          }}
                        />
                      </div>
                      <div className="filter-range-amount mt-4">
                        <p className="fs-14 price-range">
                          Khoảng:
                          <span className="text-gray-9 fw-medium">
                            {formatVND(range[0])} - {formatVND(range[1])}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="accordion-item border-bottom p-3">
                  <div className="accordion-header">
                    <button
                      className="accordion-button p-0"
                      type="button"
                      onClick={() => setTourTypeExpanded(!tourTypeExpanded)}
                    >
                      <i className="isax isax-ranking me-2 text-primary"></i>
                      Danh mục
                    </button>
                  </div>
                  {tourTypeExpanded && (
                    <div className="accordion-body pt-2">
                      <div className="more-content category-list">
                        {loading && <p>Đang tải...</p>}
                        {error && <p className="text-danger">{error}</p>}
                        {!loading &&
                          !error &&
                          categories &&
                          categories.map((category) => (
                            <div
                              key={category.id}
                              className="form-checkbox form-check form-check-inline d-inline-flex align-items-center mt-2 me-2"
                            >
                              <input
                                className="form-check-input ms-0 mt-0"
                                name={`category_${category.id}`}
                                type="checkbox"
                                id={`category_${category.id}`}
                              />
                              <label
                                className="form-check-label ms-2"
                                htmlFor={`category_${category.id}`}
                              >
                                {category.categoryName}
                              </label>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="accordion-item border-bottom p-3">
                  <div className="accordion-header">
                    <button
                      className="accordion-button p-0"
                      type="button"
                      onClick={() => setDiscountExpanded(!discountExpanded)}
                    >
                      <i className="isax isax-discount-circle me-2 text-primary"></i>
                      Theo ưu đãi & khuyến mãi
                    </button>
                  </div>
                  {discountExpanded && (
                    <div className="accordion-body mt-4">
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="discount1"
                          type="checkbox"
                          id="discount1"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="discount1"
                        >
                          Giảm giá trên 10%
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="discount2"
                          type="checkbox"
                          id="discount2"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="discount2"
                        >
                          Giảm giá trên 20%
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-0">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="discount3"
                          type="checkbox"
                          id="discount3"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="discount3"
                        >
                          Giảm giá trên 30%
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="accordion-item border-bottom p-3">
                  <div className="accordion-header">
                    <button
                      className="accordion-button p-0"
                      type="button"
                      onClick={() => setNewestExpanded(!newestExpanded)}
                    >
                      <i className="isax isax-clock me-2 text-primary"></i>
                      Tour mới nhất
                    </button>
                  </div>
                  {newestExpanded && (
                    <div className="accordion-body mt-4">
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="newest1"
                          type="checkbox"
                          id="newest1"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="newest1"
                        >
                          Trong 7 ngày qua
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="newest2"
                          type="checkbox"
                          id="newest2"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="newest2"
                        >
                          Trong 30 ngày qua
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-0">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="newest3"
                          type="checkbox"
                          id="newest3"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="newest3"
                        >
                          Trong 90 ngày qua
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="accordion-item border-bottom p-3">
                  <div className="accordion-header">
                    <button
                      className="accordion-button p-0"
                      type="button"
                      onClick={() => setBestSellerExpanded(!bestSellerExpanded)}
                    >
                      <i className="isax isax-trend-up me-2 text-primary"></i>
                      Tour bán chạy nhất
                    </button>
                  </div>
                  {bestSellerExpanded && (
                    <div className="accordion-body mt-4">
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="bestseller1"
                          type="checkbox"
                          id="bestseller1"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="bestseller1"
                        >
                          Top 10
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="bestseller2"
                          type="checkbox"
                          id="bestseller2"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="bestseller2"
                        >
                          Top 20
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-0">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="bestseller3"
                          type="checkbox"
                          id="bestseller3"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="bestseller3"
                        >
                          Top 50
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="accordion-item border-bottom p-3">
                  <div className="accordion-header">
                    <button
                      className="accordion-button p-0"
                      type="button"
                      onClick={() => setReviewsExpanded(!reviewsExpanded)}
                    >
                      <i className="isax isax-discount-shape me-2 text-primary"></i>
                      Đánh giá
                    </button>
                  </div>
                  {reviewsExpanded && (
                    <div className="accordion-body mt-4">
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="review5"
                          type="checkbox"
                          id="review5"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="review5"
                        >
                          <span className="rating d-flex align-items-center">
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary"></i>
                            <span className="ms-2">5 Sao</span>
                          </span>
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="review4"
                          type="checkbox"
                          id="review4"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="review4"
                        >
                          <span className="rating d-flex align-items-center">
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary"></i>
                            <span className="ms-2">4 Sao</span>
                          </span>
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="review3"
                          type="checkbox"
                          id="review3"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="review3"
                        >
                          <span className="rating d-flex align-items-center">
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary"></i>
                            <span className="ms-2">3 Sao</span>
                          </span>
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-2">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="review2"
                          type="checkbox"
                          id="review2"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="review2"
                        >
                          <span className="rating d-flex align-items-center">
                            <i className="fas fa-star filled text-primary me-1"></i>
                            <i className="fas fa-star filled text-primary"></i>
                            <span className="ms-2">2 Sao</span>
                          </span>
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center ps-0 mb-0">
                        <input
                          className="form-check-input ms-0 mt-0"
                          name="review1"
                          type="checkbox"
                          id="review1"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="review1"
                        >
                          <span className="rating d-flex align-items-center">
                            <i className="fas fa-star filled text-primary"></i>
                            <span className="ms-2">1 Sao</span>
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
